import { useMemo } from 'react';
import { usePersistedState } from './persisted-state';

export const DEFAULT_TIMEZONE = 'Asia/Singapore';

/** Common IANA zones for workspace display (reports, check-in dashboards). */
export const commonTimezones = [
  { value: 'Asia/Singapore', label: 'Asia/Singapore (GMT+8)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Asia/Kuala_Lumpur', label: 'Asia/Kuala_Lumpur (GMT+8)' },
  { value: 'Asia/Bangkok', label: 'Asia/Bangkok (GMT+7)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (GMT+9)' },
  { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong (GMT+8)' },
  { value: 'Europe/London', label: 'Europe/London' },
  { value: 'Europe/Paris', label: 'Europe/Paris' },
  { value: 'America/New_York', label: 'America/New_York' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney' },
];

export function resolveTimezone(timezone) {
  const t = String(timezone || '').trim();
  const candidate = t || DEFAULT_TIMEZONE;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: candidate });
    return candidate;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

/**
 * Format an ISO UTC timestamp in the given IANA timezone.
 */
export function formatInTimezone(iso, timezone, options = {}) {
  if (!iso) return '—';
  const tz = resolveTimezone(timezone);
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      ...options,
    }).format(d);
  } catch {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return '—';
    }
  }
}

/** Convert UTC ISO to `datetime-local` value interpreted in workspace timezone. */
export function isoToDatetimeLocal(iso, timezone) {
  if (!iso) return '';
  const tz = resolveTimezone(timezone);
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

/** Convert `datetime-local` value (workspace timezone) to UTC ISO for API filters. */
export function datetimeLocalToIso(localValue, timezone) {
  if (!localValue) return '';
  const [datePart, timePart] = localValue.split('T');
  if (!datePart || !timePart) return '';
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  if ([y, m, d, hh, mm].some((n) => Number.isNaN(n))) return '';

  const tz = resolveTimezone(timezone);
  let candidate = new Date(Date.UTC(y, m - 1, d, hh, mm));
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  for (let i = 0; i < 4; i += 1) {
    const parts = formatter.formatToParts(candidate);
    const cy = Number(parts.find((p) => p.type === 'year')?.value);
    const cmo = Number(parts.find((p) => p.type === 'month')?.value);
    const cd = Number(parts.find((p) => p.type === 'day')?.value);
    const ch = Number(parts.find((p) => p.type === 'hour')?.value);
    const cmin = Number(parts.find((p) => p.type === 'minute')?.value);
    const diffMin = (y - cy) * 525600 + (m - cmo) * 43200 + (d - cd) * 1440 + (hh - ch) * 60 + (mm - cmin);
    if (diffMin === 0) break;
    candidate = new Date(candidate.getTime() + diffMin * 60000);
  }
  return candidate.toISOString();
}

export function timezoneLabel(timezone) {
  const tz = resolveTimezone(timezone);
  const match = commonTimezones.find((z) => z.value === tz);
  return match?.label || tz;
}

/** Workspace org.timezone from persisted settings (default Asia/Singapore). */
export function useWorkspaceTimezone() {
  const { value } = usePersistedState('workspace.settings', {
    org: { timezone: DEFAULT_TIMEZONE },
  });
  return useMemo(
    () => resolveTimezone(value?.org?.timezone),
    [value?.org?.timezone],
  );
}
